import { createFileRoute, Link } from '@tanstack/react-router'
import { Plus, Search, Users, UserPlus } from 'lucide-react'
import { useState } from 'react'

import {
  EmptyState,
  NoResultsEmptyState,
  determineEmptyStateType,
} from '@/components/common/empty-state'
import { EmployeeDashboard } from '@/components/hr/employee/employee-dashboard'
import { EmployeeForm } from '@/components/hr/employee/employee-form'
import { EmployeeQuickActions, EmployeeStatusBadge } from '@/components/hr/employee/employee-quick-actions'
import { OffboardingWizard } from '@/components/hr/employee/offboarding-wizard'
import { OnboardingWizard } from '@/components/hr/employee/onboarding-wizard'
import {
  ResponsiveListContainer,
  ListHeader,
  FilterBar,
  SearchBox,
  ResponsiveTable,
  PaginationContainer,
} from '@/components/ui/responsive-list'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { TableRowsSkeleton } from '@/components/ui/skeleton'
import { useDebouncedValue } from '@/hooks'
import {
  useEmployees,
  type Employee,
  type EmployeeStatus,
  type CompanyRole,
  type EmployeeListParams,
} from '@/hooks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/hr/employees/')({
  component: EmployeeListPage,
})

const STATUS_LABELS: Record<EmployeeStatus, string> = {
  active: '在职',
  on_leave: '休假中',
  resigned: '已离职',
}

const ROLE_LABELS: Record<CompanyRole, string> = {
  director: '主管',
  finance: '财务',
  hr: '人事',
  admin: '行政',
  ops: '运维',
  pm: '项目经理',
  developer: '开发人员',
}

function EmployeeListPage() {
  const [filters, setFilters] = useState<EmployeeListParams>({ page: 1, pageSize: 20 })
  const [searchInput, setSearchInput] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null)
  const [offboardingEmployee, setOffboardingEmployee] = useState<Employee | null>(null)

  // 使用防抖搜索
  const debouncedSearch = useDebouncedValue(searchInput, 300)

  const { data, isLoading, error } = useEmployees({
    ...filters,
    search: debouncedSearch || undefined,
  })

  const handleStatusFilter = (status: EmployeeStatus | undefined) => {
    setFilters((prev) => ({ ...prev, status, page: 1 }))
  }

  const handleRoleFilter = (role: CompanyRole | undefined) => {
    setFilters((prev) => ({ ...prev, role, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  const employees = data?.data ?? []
  const meta = data?.meta

  return (
    <ResponsiveListContainer>
      {/* 页面标题 */}
      <ListHeader
        title="员工管理"
        description="管理公司员工信息"
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowOnboardingWizard(true)}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <UserPlus className="h-4 w-4" />
              <span className="hidden sm:inline">入职向导</span>
              <span className="sm:hidden">入职</span>
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">快速新建</span>
            </button>
          </div>
        }
      />

      {/* 状态看板 */}
      <EmployeeDashboard />

      {/* 新建员工表单 */}
      {showCreateForm && (
        <EmployeeForm onClose={() => setShowCreateForm(false)} onSuccess={() => setShowCreateForm(false)} />
      )}

      {/* 入职向导 */}
      {showOnboardingWizard && (
        <OnboardingWizard onClose={() => setShowOnboardingWizard(false)} onSuccess={() => setShowOnboardingWizard(false)} />
      )}

      {/* 编辑员工表单 */}
      {editingEmployee && (
        <EmployeeForm employee={editingEmployee} onClose={() => setEditingEmployee(null)} onSuccess={() => setEditingEmployee(null)} />
      )}

      {/* 离职向导 */}
      {offboardingEmployee && (
        <OffboardingWizard
          employee={offboardingEmployee}
          onClose={() => setOffboardingEmployee(null)}
          onSuccess={() => setOffboardingEmployee(null)}
        />
      )}

      {/* 筛选和搜索 */}
      <FilterBar>
        <SearchBox>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="搜索员工姓名、邮箱..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </SearchBox>

        {/* 状态筛选 - 移动端使用下拉框 */}
        <div className="flex items-center gap-2 sm:hidden">
          <Select
            value={filters.status ?? 'all'}
            onValueChange={(value) => handleStatusFilter(value === 'all' ? undefined : value as EmployeeStatus)}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="全部状态" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部状态</SelectItem>
              {(Object.keys(STATUS_LABELS) as EmployeeStatus[]).map((status) => (
                <SelectItem key={status} value={status}>{STATUS_LABELS[status]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 状态筛选 - 桌面端使用按钮组 */}
        <div className="hidden sm:flex items-center gap-2">
          <button
            onClick={() => handleStatusFilter(undefined)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              !filters.status ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            全部
          </button>
          {(Object.keys(STATUS_LABELS) as EmployeeStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => handleStatusFilter(status)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                filters.status === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              {STATUS_LABELS[status]}
            </button>
          ))}
        </div>

        {/* 角色筛选 */}
        <Select
          value={filters.role ?? 'all'}
          onValueChange={(value) => handleRoleFilter(value === 'all' ? undefined : value as CompanyRole)}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="全部角色" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部角色</SelectItem>
            {(Object.keys(ROLE_LABELS) as CompanyRole[]).map((role) => (
              <SelectItem key={role} value={role}>
                {ROLE_LABELS[role]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterBar>

      {/* 员工列表 */}
      {isLoading ? (
        <TableRowsSkeleton rows={5} columns={7} />
      ) : employees.length === 0 ? (
        // Requirements 4.1, 4.2, 4.3, 4.4 - 使用增强空状态组件
        (() => {
          const hasFilters = Boolean(filters.status || filters.role || debouncedSearch)
          const emptyStateType = determineEmptyStateType({
            hasData: false,
            hasFilters,
            hasError: false,
            isFirstVisit: false,
          })
          
          if (emptyStateType === 'no-results') {
            return (
              <NoResultsEmptyState
                title="未找到匹配的员工"
                description="尝试调整筛选条件或搜索关键词"
                onClearFilters={() => {
                  setFilters({ page: 1, pageSize: 20 })
                  setSearchInput('')
                }}
              />
            )
          }
          
          return (
            <EmptyState
              type={emptyStateType}
              title="暂无员工"
              description="点击上方按钮创建第一个员工"
              icon={Users}
              primaryAction={{
                label: '入职向导',
                onClick: () => setShowOnboardingWizard(true),
              }}
            />
          )
        })()
      ) : (
        <ResponsiveTable>
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium hidden lg:table-cell">员工编号</th>
                <th className="px-4 py-3 text-left text-sm font-medium">姓名</th>
                <th className="px-4 py-3 text-left text-sm font-medium hidden md:table-cell">邮箱</th>
                <th className="px-4 py-3 text-left text-sm font-medium hidden sm:table-cell">角色</th>
                <th className="px-4 py-3 text-left text-sm font-medium">状态</th>
                <th className="px-4 py-3 text-left text-sm font-medium hidden lg:table-cell">入职日期</th>
                <th className="px-4 py-3 text-left text-sm font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => (
                <tr key={emp.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3 text-sm font-mono hidden lg:table-cell">{emp.employeeNo}</td>
                  <td className="px-4 py-3">
                    <Link
                      to="/hr/employees/$employeeId"
                      params={{ employeeId: emp.id }}
                      className="font-medium hover:text-primary hover:underline"
                    >
                      {emp.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">{emp.email}</td>
                  <td className="px-4 py-3 text-sm hidden sm:table-cell">{ROLE_LABELS[emp.role]}</td>
                  <td className="px-4 py-3">
                    <EmployeeStatusBadge status={emp.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground hidden lg:table-cell">
                    {new Date(emp.hireDate).toLocaleDateString('zh-CN')}
                  </td>
                  <td className="px-4 py-3">
                    <EmployeeQuickActions
                      employee={emp}
                      onEdit={() => setEditingEmployee(emp)}
                      onStartOffboarding={() => setOffboardingEmployee(emp)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ResponsiveTable>
      )}

      {/* 分页 */}
      {meta && meta.totalPages > 1 && (
        <PaginationContainer
          info={`共 ${meta.total} 条记录，第 ${meta.page}/${meta.totalPages} 页`}
        >
          <button
            onClick={() => handlePageChange(meta.page - 1)}
            disabled={meta.page <= 1}
            className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
          >
            上一页
          </button>
          <button
            onClick={() => handlePageChange(meta.page + 1)}
            disabled={meta.page >= meta.totalPages}
            className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
          >
            下一页
          </button>
        </PaginationContainer>
      )}
    </ResponsiveListContainer>
  )
}
